import { useState, useRef, useEffect, memo } from 'react';
import type { BroadcastGroupSummary } from '@/types/broadcast';
import { Badge } from '@/components/ui/badge';
import { getStatusColor, getStatusLabel, formatInTz } from '../utils/broadcastUtils';
import { Calendar, MoreVertical, Tag, Copy, Languages, Ban } from 'lucide-react';

interface BroadcastGroupGridProps {
    groups: BroadcastGroupSummary[];
    onView?: (group: BroadcastGroupSummary) => void; // TODO: rota desabilitada temporariamente
    onDuplicate: (group: BroadcastGroupSummary) => void;
    onCancel: (group: BroadcastGroupSummary) => void;
}

const CANCELLABLE_STATUSES = new Set(['pending', 'processing', 'translating', 'sending', 'sent']);

function CircleProgress({ percent, status }: { percent: number; status: string }) {
    const r = 18;
    const circ = 2 * Math.PI * r;
    const offset = circ - (percent / 100) * circ;

    const strokeColor =
        status === 'processing' ? '#06b6d4' :
            status === 'sent' ? '#10b981' :
                status === 'partial' ? '#f59e0b' :
                    status === 'failed' ? '#ef4444' :
                        '#f59e0b';

    return (
        <svg width="44" height="44" viewBox="0 0 44 44">
            <circle cx="22" cy="22" r={r} fill="none" stroke="#e5e7eb" strokeWidth="3.5" />
            <circle
                cx="22" cy="22" r={r} fill="none"
                stroke={strokeColor} strokeWidth="3.5"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 22 22)"
            />
            <text x="22" y="26" textAnchor="middle" fontSize="9" fontWeight="600" fill="#374151">
                {Math.round(percent)}%
            </text>
        </svg>
    );
}

function RowMenu({ onDuplicate, onCancel, canCancel }: { onView?: () => void; onDuplicate: () => void; onCancel: () => void; canCancel: boolean }) { // onView removido (TODO: rota desabilitada temporariamente)
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <MoreVertical className="w-4 h-4" />
            </button>
            {open && (
                <div className="absolute right-0 top-8 z-20 bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[180px]">
                    {/* TODO: rota desabilitada temporariamente */}
                    {/* <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onView(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Eye className="w-4 h-4 text-gray-400" />
                        Ver broadcast
                    </button> */}
                    <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onDuplicate(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Copy className="w-4 h-4 text-gray-400" />
                        Duplicar
                    </button>
                    {canCancel && (
                        <button
                            onClick={e => { e.stopPropagation(); setOpen(false); onCancel(); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <Ban className="w-4 h-4 text-red-400" />
                            Cancelar grupo
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

const BRT_TZ = 'America/Sao_Paulo';

const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return formatInTz(dateString, BRT_TZ, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

interface GroupRowProps {
    group: BroadcastGroupSummary;
    onView?: (group: BroadcastGroupSummary) => void; // TODO: rota desabilitada temporariamente
    onDuplicate: (group: BroadcastGroupSummary) => void;
    onCancel: (group: BroadcastGroupSummary) => void;
}

const GroupRow = memo(function GroupRow({ group, onView, onDuplicate, onCancel }: GroupRowProps) {
    const status = group.status;
    const progressPercent = group.success_rate ?? 0;
    const scheduledAt = group.scheduled_at;
    const pagesCount = group.total_pages;
    const totalProfilesSent = group.total_sent;
    const creatorName = group.user_name || null;
    const displayName = group.title || group.name || `Grupo #${group.group_broadcast_id}`;

    const tags = group.message_tag ? [group.message_tag] : [];
    // Um grupo cobre países diferentes: 14 idiomas é o normal, não a exceção.
    // `target_language` (singular) é só o primeiro em ordem alfabética — o card
    // mostrava "Alemão - Alemanha" num grupo com 14 idiomas.
    const aiLanguages = group.ai_translate
        ? (group.target_languages?.length ? group.target_languages
            : group.target_language ? [group.target_language] : [])
        : [];
    const aiLabel = aiLanguages.length === 1 ? aiLanguages[0] : `${aiLanguages.length} idiomas`;

    return (
        <div
            className="group/row relative grid grid-cols-[1fr_140px_200px_40px] px-6 py-4 items-center transition-all duration-150 hover:bg-gradient-to-r hover:from-slate-50 hover:to-transparent cursor-pointer"
            onClick={() => onView?.(group)}
        >
            {/* left accent on hover */}
            <span className="absolute left-0 top-0 h-full w-[3px] rounded-r bg-blue-400 opacity-0 group-hover/row:opacity-100 transition-opacity duration-150" />

            {/* Fluxo */}
            <div className="pr-6 min-w-0">
                <div className="mb-1">
                    <Badge className={`${getStatusColor(status, !!scheduledAt)} border rounded-full text-[11px] px-2 py-0 font-medium`}>
                        {getStatusLabel(status, !!scheduledAt)}
                    </Badge>
                </div>
                <p
                    className="text-[15px] font-bold text-gray-900 truncate mb-1.5 transition-colors group-hover/row:text-gray-700"
                    title="Clique para duplicar"
                    onClick={e => { e.stopPropagation(); onDuplicate(group); }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#2563eb')}
                    onMouseLeave={e => (e.currentTarget.style.color = '')}
                    style={{ cursor: 'pointer' }}
                >
                    {displayName}
                </p>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {tags.map((tag, i) => (
                        <span key={`tag-${i}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-100/50 rounded-md px-2 py-0.5">
                            <Tag className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
                            {tag}
                        </span>
                    ))}
                    {aiLanguages.length > 0 && (
                        <span
                            title={aiLanguages.join(', ')}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-600 bg-cyan-50 border border-cyan-100/50 rounded-md px-2 py-0.5"
                        >
                            <Languages className="w-3 h-3 text-cyan-400" strokeWidth={1.5} />
                            IA: {aiLabel}
                        </span>
                    )}
                    {group.utility_template_id && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-600 bg-purple-50 border border-purple-100/50 rounded-md px-2 py-0.5">
                            Template #{group.utility_template_id}
                        </span>
                    )}
                    <span className="inline-flex items-center text-[11px] font-medium text-blue-600 bg-blue-50 border border-blue-100/50 rounded-md px-2 py-0.5">
                        {pagesCount} {pagesCount === 1 ? 'página' : 'páginas'}
                    </span>
                </div>
            </div>

            {/* Progresso */}
            <div className="flex items-center gap-3">
                <CircleProgress percent={progressPercent} status={status} />
                <div>
                    <p className="text-sm font-semibold text-gray-800">
                        {totalProfilesSent.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-[11px] text-gray-400">{Math.round(group.success_rate ?? 0)}% sucesso</p>
                </div>
            </div>

            {/* Agendamento */}
            <div className="text-sm">
                {scheduledAt ? (
                    <div className="flex items-center gap-1.5 text-gray-700 mb-0.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" strokeWidth={1.5} />
                        <span className="font-medium">{formatDate(scheduledAt)} <span className="text-[10px] text-gray-400 font-normal">BRT</span></span>
                    </div>
                ) : (
                    <span className="text-gray-400 text-xs">Sem agendamento</span>
                )}
                {creatorName && (
                    <p className="text-[12px] text-gray-400 truncate max-w-[180px]">
                        Por {creatorName}
                    </p>
                )}
            </div>

            {/* Menu */}
            <div className="flex justify-end" onClick={e => e.stopPropagation()}>
                <RowMenu
                    onView={() => { /* TODO: rota desabilitada temporariamente */ }}
                    onDuplicate={() => onDuplicate(group)}
                    onCancel={() => onCancel(group)}
                    canCancel={CANCELLABLE_STATUSES.has(status)}
                />
            </div>
        </div>
    );
});

export default function BroadcastGroupGrid({ groups, onView, onDuplicate, onCancel }: BroadcastGroupGridProps) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100">
            {/* Header */}
            <div className="grid grid-cols-[1fr_140px_200px_40px] px-6 py-3 border-b border-gray-100 bg-gray-50/60">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Fluxo</span>
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Progresso</span>
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Agendamento</span>
                <span />
            </div>

            {/* Rows */}
            <div className="divide-y divide-gray-100">
                {groups.map((group) => (
                    <GroupRow
                        key={group.group_broadcast_id}
                        group={group}
                        onView={onView}
                        onDuplicate={onDuplicate}
                        onCancel={onCancel}
                    />
                ))}
            </div>
        </div>
    );
}
